import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "@freetech/models/db";
import { DateTime } from "luxon";
import { MoveCardParams } from "types/freelo/crud";
import { QueryClient } from "@tanstack/react-query";
import { User } from "firebase/auth";
import { NestedBoard } from "types/freelo/nested";

export const moveCard = async (params: MoveCardParams, queryClient: QueryClient, currentUser: User): Promise<void> => {
  const { boardId, cardId, sourceListId, destinationListId, destinationIndex = 0 } = params;

  const previousBoard = queryClient.getQueryData<NestedBoard>(["board", boardId]);
  if (!previousBoard) return;

  try {
    const updatePromises = [];

    // Update card's listId if moving between lists
    if (sourceListId !== destinationListId) {
      const cardRef = doc(db.cards(boardId), cardId);
      updatePromises.push(
        updateDoc(cardRef, {
          listId: destinationListId,
          updatedAt: DateTime.now().toISO(),
          updatedBy: currentUser.uid,
        })
      );
    }

    if (sourceListId === destinationListId) {
      // Same list - just reorder
      const sourceList = previousBoard.lists?.find(list => list.id === sourceListId);
      if (sourceList) {
        const sourceCardOrder = [...(sourceList.cardOrder || [])];
        const cardIndex = sourceCardOrder.indexOf(cardId);
        if (cardIndex > -1) {
          sourceCardOrder.splice(cardIndex, 1);
          sourceCardOrder.splice(destinationIndex, 0, cardId);
        }

        const sourceListRef = doc(db.lists(boardId), sourceListId);
        updatePromises.push(
          updateDoc(sourceListRef, {
            cardOrder: sourceCardOrder,
            updatedAt: DateTime.now().toISO(),
            updatedBy: currentUser.uid,
          })
        );
      }
    } else {
      // Different lists - update both source and destination
      const sourceList = previousBoard.lists?.find(list => list.id === sourceListId);
      const destList = previousBoard.lists?.find(list => list.id === destinationListId);

      if (sourceList) {
        const sourceCardOrder = [...(sourceList.cardOrder || [])];
        const cardIndex = sourceCardOrder.indexOf(cardId);
        if (cardIndex > -1) {
          sourceCardOrder.splice(cardIndex, 1);
        }

        const sourceListRef = doc(db.lists(boardId), sourceListId);
        updatePromises.push(
          updateDoc(sourceListRef, {
            cardOrder: sourceCardOrder,
            updatedAt: DateTime.now().toISO(),
            updatedBy: currentUser.uid,
          })
        );
      }

      if (destList) {
        const destCardOrder = [...(destList.cardOrder || [])];
        destCardOrder.splice(destinationIndex, 0, cardId);

        const destListRef = doc(db.lists(boardId), destinationListId);
        updatePromises.push(
          updateDoc(destListRef, {
            cardOrder: destCardOrder,
            updatedAt: DateTime.now().toISO(),
            updatedBy: currentUser.uid,
          })
        );
      }
    }

    await Promise.all(updatePromises);
  } catch (error) {
    // Revert optimistic updates on error
    queryClient.invalidateQueries({ queryKey: ["board", boardId] });
    throw error;
  }
};
